import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, ExternalLink, Loader2, FileText, Eye } from "lucide-react";
import api, { formatApiErrorDetail } from "../../lib/api";
import { useLang } from "../../context/LangContext";
import { Button } from "../../components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../../components/ui/alert-dialog";
import BlogBadge from "../../components/BlogBadge";

export default function PublishedTab() {
  const { t, lang } = useLang();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [confirm, setConfirm] = useState(null);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/articles"); setItems(data); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const del = async (a) => {
    setBusy(a.id);
    try { await api.delete(`/articles/${a.id}`); toast.success(t("Artigo apagado", "Article deleted")); await load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(null); setConfirm(null); }
  };

  return (
    <div>
      <h2 className="font-heading text-2xl font-bold mb-1">{t("Artigos publicados", "Published articles")}</h2>
      <p className="text-sm text-muted-foreground mb-6">{t("Gere e remove os artigos que estão no site público.", "Manage and remove articles that are live on the public site.")}</p>

      {loading ? <p className="text-muted-foreground">{t("A carregar…", "Loading…")}</p> : items.length === 0 ? (
        <div className="rounded-2xl border border-white/10 bg-card p-12 text-center text-muted-foreground">{t("Ainda não há artigos publicados.", "No published articles yet.")}</div>
      ) : (
        <div className="grid gap-4">
          {items.map((a) => (
            <div key={a.id} data-testid={`published-card-${a.id}`} className="rounded-2xl border border-white/10 bg-card p-5 flex items-center gap-4">
              {a.images?.hero ? <img src={a.images.hero} alt="" className="h-16 w-24 object-cover rounded-lg border border-white/10" /> : <div className="h-16 w-24 grid place-items-center rounded-lg bg-white/5"><FileText className="h-5 w-5 text-muted-foreground" /></div>}
              <div className="flex-1 min-w-0">
                <BlogBadge blog={a.blog} />
                <h3 className="mt-2 font-heading text-lg font-semibold truncate">{a.title}</h3>
                <p className="flex items-center gap-3 text-xs text-muted-foreground">
                  <span>{new Date(a.published_at).toLocaleString(lang === "pt" ? "pt-PT" : "en-GB")}</span>
                  <span className="inline-flex items-center gap-1" data-testid={`published-views-${a.id}`}><Eye className="h-3.5 w-3.5" />{a.views || 0}</span>
                </p>
              </div>
              <a href={`/blog/${a.blog}/${a.slug}`} target="_blank" rel="noreferrer" className="rounded-lg p-2 text-muted-foreground hover:text-foreground hover:bg-white/5" title={t("Ver", "View")} data-testid={`published-view-${a.id}`}><ExternalLink className="h-4 w-4" /></a>
              <Button data-testid={`published-delete-${a.id}`} size="sm" variant="ghost" className="gap-1.5 text-destructive hover:text-destructive" onClick={() => setConfirm(a)} disabled={busy === a.id}>
                {busy === a.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}{t("Apagar", "Delete")}
              </Button>
            </div>
          ))}
        </div>
      )}

      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("Apagar artigo?", "Delete article?")}</AlertDialogTitle>
            <AlertDialogDescription>{t("Esta ação remove o artigo do site público e não pode ser desfeita.", "This removes the article from the public site and cannot be undone.")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("Cancelar", "Cancel")}</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-article" className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={() => confirm && del(confirm)}>{t("Apagar", "Delete")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
